import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { FaEnvelope, FaIdCard, FaPhone, FaUser } from "react-icons/fa";
import InputMask from "react-input-mask";
import Menu from "../../components/Menu";
import NavbarDashboard from "../../components/NavbarDashboard";
import { useUserProfile } from "../../hooks/useUserProfile";

function MyProfile() {
  const { profile, isLoading, updateProfile, isUpdating } = useUserProfile();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    cpf: "",
    cellphone: "",
  });

  const [errors, setErrors] = useState({});

  // Popula o formulário quando os dados chegam
  useEffect(() => {
    if (profile?.data) {
      setFormData({
        name: profile.data.name || "",
        email: profile.data.email || "",
        cpf: profile.data.cpf || "",
        cellphone: profile.data.cellphone || "",
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Limpa o erro do campo quando o usuário começa a digitar
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    } else if (formData.name.length > 255) {
      newErrors.name = "Nome deve ter no máximo 255 caracteres";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (formData.cpf && formData.cpf.length > 14) {
      newErrors.cpf = "CPF deve ter no máximo 14 caracteres";
    }

    if (formData.cellphone && formData.cellphone.length > 15) {
      newErrors.cellphone = "Celular deve ter no máximo 15 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Envia apenas os campos que foram preenchidos
    const dataToSend = {};
    Object.keys(formData).forEach((key) => {
      if (formData[key].trim()) {
        dataToSend[key] = formData[key].trim();
      }
    });

    updateProfile(dataToSend);
  };

  const getPlanName = (planId) => {
    const plans = {
      1: "Free",
      2: "Start",
      3: "Gold",
      4: "Premium",
      5: "Custom",
    };
    return plans[planId] || "Desconhecido";
  };

  if (isLoading) {
    return (
      <>
        <NavbarDashboard />
        <Menu />
        <section className="home">
          <Container className="p-5">
            <div className="text-center">
              <Spinner animation="border" variant="warning" />
              <p className="mt-3">Carregando perfil...</p>
            </div>
          </Container>
        </section>
      </>
    );
  }

  return (
    <>
      <NavbarDashboard />
      <Menu />
      <section className="home">
        <Container className="p-5">
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <Card
                style={{
                  border: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Card.Body className="p-4">
                  <div className="text-center mb-4">
                    <FaUser size={48} className="text-warning mb-3" />
                    <h2 className="mb-2">Meu Perfil</h2>
                    <p className="text-muted">
                      Gerencie suas informações pessoais
                    </p>
                  </div>

                  {/* Plano Atual */}
                  {profile?.data?.plan_id && (
                    <Alert variant="info" className="mb-4">
                      <strong>Plano Atual:</strong>{" "}
                      {getPlanName(profile.data.plan_id)}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    {/* Nome */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaUser className="me-2" />
                        Nome Completo
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                        maxLength={255}
                        placeholder="Digite seu nome completo"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Email */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaEnvelope className="me-2" />
                        Email
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                        placeholder="seu@email.com"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* CPF */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaIdCard className="me-2" />
                        CPF
                      </Form.Label>
                      <InputMask
                        mask="999.999.999-99"
                        value={formData.cpf}
                        onChange={handleChange}
                        disabled={!!profile?.data?.cpf}
                        className={`form-control ${errors.cpf ? 'is-invalid' : ''}`}
                      >
                        {(inputProps) => (
                          <Form.Control
                            {...inputProps}
                            type="text"
                            name="cpf"
                            placeholder="000.000.000-00"
                            disabled={!!profile?.data?.cpf}
                          />
                        )}
                      </InputMask>
                      <Form.Control.Feedback type="invalid">
                        {errors.cpf}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        {profile?.data?.cpf 
                          ? "CPF não pode ser alterado após cadastrado"
                          : "Formato: 000.000.000-00"}
                      </Form.Text>
                    </Form.Group>

                    {/* Celular */}
                    <Form.Group className="mb-4">
                      <Form.Label>
                        <FaPhone className="me-2" />
                        Celular
                      </Form.Label>
                      <InputMask
                        mask="(99) 99999-9999"
                        value={formData.cellphone}
                        onChange={handleChange}
                        className={`form-control ${errors.cellphone ? 'is-invalid' : ''}`}
                      >
                        {(inputProps) => (
                          <Form.Control
                            {...inputProps}
                            type="text"
                            name="cellphone"
                            placeholder="(00) 00000-0000"
                          />
                        )}
                      </InputMask>
                      <Form.Control.Feedback type="invalid">
                        {errors.cellphone}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Formato: (00) 00000-0000
                      </Form.Text>
                    </Form.Group>

                    {/* Botões */}
                    <div className="d-grid gap-2">
                      <Button
                        className="d-flex justify-content-center shadow-sm fs-6 p-2 rounded-2 fw-normal text-dark borderColor"
                        style={{ backgroundColor: "#DAA520" }}
                        disabled={isUpdating}
                        type="submit"
                      >
                        {isUpdating ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              className="me-2"
                            />
                            Salvando...
                          </>
                        ) : (
                          "Salvar Alterações"
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

export default MyProfile;
